import * as Sentry from '@sentry/react';

type FirebaseErrorLike = {
  code?: unknown;
};

export type PhoneVerificationFlow =
  | 'mfa-enrollment-start'
  | 'mfa-enrollment-resend'
  | 'mfa-enrollment-finalize'
  | 'mfa-sign-in-start'
  | 'mfa-sign-in-finalize';

export type ReportedPhoneVerificationError = {
  incidentId: string;
  userMessage: string;
};

type PhoneVerificationErrorContext = {
  suspectedLifecellPrefix?: boolean;
};

export const getFirebaseAuthErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const { code } = error as FirebaseErrorLike;
  return typeof code === 'string' && code.startsWith('auth/') ? code : null;
};

export const reportPhoneVerificationError = (
  error: unknown,
  flow: PhoneVerificationFlow,
  context?: PhoneVerificationErrorContext,
): ReportedPhoneVerificationError | null => {
  const firebaseCode = getFirebaseAuthErrorCode(error);
  if (!firebaseCode) {
    return null;
  }

  // Report a sanitized error: Firebase errors can contain request metadata, and
  // phone verification requests contain phone numbers and authentication tokens.
  const reportedError = new Error(
    `Firebase phone verification failed (${firebaseCode})`,
  );
  reportedError.name = 'FirebasePhoneVerificationError';

  // Preserve useful call frames without copying Firebase's original message.
  if (error instanceof Error && error.stack) {
    const callFrames = error.stack.split('\n').slice(1);
    reportedError.stack = `${reportedError.name}: ${
      reportedError.message
    }\n${callFrames.join('\n')}`;
  }

  const incidentId = Sentry.captureException(reportedError, {
    tags: {
      feature: 'firebase-phone-verification',
      firebase_error_code: firebaseCode,
      phone_verification_flow: flow,
      ...(context?.suspectedLifecellPrefix
        ? { suspected_lifecell_prefix: true }
        : {}),
    },
  });

  const isSendingCode = flow.endsWith('-start') || flow.endsWith('-resend');

  // Firebase has a known unresolved issue where SMS requests for some Ukrainian
  // Lifecell subscribers fail upstream with HTTP 500 and only surface to clients
  // as the generic `auth/internal-error` code:
  // https://github.com/firebase/flutterfire/issues/13103
  // Keep the failure message carrier-neutral because a prefix does not prove the
  // current carrier. The enrollment UI may show a separate prefix-based warning.
  const userMessage =
    firebaseCode === 'auth/internal-error' && isSendingCode
      ? 'We could not send a verification code. Your mobile carrier may not be supported. Try another phone number or contact support.'
      : isSendingCode
      ? 'We could not send the verification code. Please try again or contact support.'
      : 'We could not complete phone verification. Please try again or contact support.';

  return { incidentId, userMessage };
};
